// Ask Hopscotch — responsive composition + visibility gate.
//
// Suppressed entirely (no handle, no panel) while a game is being played
// and during the evaluation flow. Otherwise:
//   push    (≥1200px)  — closed: right-edge handle · open: 380px rail that
//                        is a flex sibling of the app, so content REFLOWS
//   overlay (768–1199) — closed: handle · open: 360px fixed panel over a
//                        dimmed app (scrim)
//   sheet   (<768)     — closed: bottom-right ink "Ask" pill · open: full-
//                        height bottom sheet below the status bar
//
// Streams are never aborted by suppression or collapse — thread writes are
// key-captured (useCompanionChat), and unread replies light the dot.

import { useEffect, useRef } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { useGlobalState } from '@/stores/globalState'
import { useCompanionMode } from '@/hooks/useMediaQuery'
import { useCompanionSuppressed } from '@/lib/chatContext'
import { CompanionHandle, CompanionMobileLauncher } from './CompanionLaunchers'
import { CompanionPanel } from './CompanionPanel'
import './styles/CompanionRoot.css'

const PANEL_W_PUSH = 380

export function CompanionRoot() {
  const suppressed = useCompanionSuppressed()
  const mode = useCompanionMode()
  const open = useGlobalState((s) => s.companionOpen)
  const unread = useGlobalState((s) => s.companionUnread) > 0
  const setCompanionOpen = useGlobalState((s) => s.setCompanionOpen)
  const reducedMotion = useReducedMotion()

  // Focus returns to the launcher when the panel closes (keyboard flow:
  // Esc closes → focus lands back where it started).
  const launcherRef = useRef<HTMLButtonElement>(null)
  const prevOpenRef = useRef(open)
  useEffect(() => {
    if (prevOpenRef.current && !open) launcherRef.current?.focus()
    prevOpenRef.current = open
  }, [open])

  if (suppressed) return null

  const openPanel = () => setCompanionOpen(true)
  const closePanel = () => setCompanionOpen(false)
  const duration = reducedMotion ? 0 : 0.24

  if (mode === 'push') {
    return (
      <>
        {!open && <CompanionHandle ref={launcherRef} onClick={openPanel} unread={unread} />}
        <AnimatePresence initial={false}>
          {open && (
            <motion.aside
              key="companion-rail"
              initial={{ width: 0 }}
              animate={{ width: PANEL_W_PUSH }}
              exit={{ width: 0 }}
              transition={{ duration, ease: 'easeOut' }}
              className="h-full companion-root__push-rail"
            >
              <div className="companion-root__push-inner">
                <CompanionPanel onClose={closePanel} />
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </>
    )
  }

  if (mode === 'overlay') {
    return (
      <>
        {!open && <CompanionHandle ref={launcherRef} onClick={openPanel} unread={unread} />}
        <AnimatePresence>
          {open && (
            <>
              <motion.div
                key="companion-scrim"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration }}
                onClick={closePanel}
                aria-hidden="true"
                className="companion-root__scrim companion-root__scrim--overlay"
              />
              <motion.aside
                key="companion-overlay"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ duration, ease: 'easeOut' }}
                className="companion-root__overlay"
              >
                <CompanionPanel onClose={closePanel} />
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </>
    )
  }

  // sheet (phone)
  return (
    <>
      {!open && <CompanionMobileLauncher ref={launcherRef} onClick={openPanel} unread={unread} />}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="companion-scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration }}
              onClick={closePanel}
              aria-hidden="true"
              className="companion-root__scrim companion-root__scrim--sheet"
            />
            <motion.div
              key="companion-sheet"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: reducedMotion ? 0 : 0.26, ease: 'easeOut' }}
              className="companion-root__sheet"
            >
              <div
                aria-hidden="true"
                className="companion-root__sheet-grip"
              />
              <CompanionPanel onClose={closePanel} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
